import { getFormattedDateTimeString, TimeFormat } from '@chronosphereio/core';
import { FormatOverrides, TableFormatOverrideKind } from '@/core';

/**
 * Check if a value can be parsed as a time/date
 */
export function canParseAsTime(value: unknown): boolean {
  if (typeof value === 'number') {
    return !isNaN(value) && isFinite(value); // Unix timestamp must be valid number
  }
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return !isNaN(parsed);
  }
  return false;
}

/**
 * Check if a value can be parsed as a number
 */
export function canParseAsNumber(value: unknown): boolean {
  if (typeof value === 'number') return true;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return false;
    const parsed = parseFloat(trimmed);
    return !isNaN(parsed);
  }
  return false;
}

/**
 * Get valid format kinds for a column by sampling first 5 non-null values
 */
export function getValidFormatKinds(
  columnField: string,
  rows: Array<Record<string, unknown>>,
  originalFormat: TableFormatOverrideKind
): TableFormatOverrideKind[] {
  const validKinds = [originalFormat]; // Always include original format type

  // Sample first 5 non-null values
  const samples = rows
    .map((row) => row[columnField])
    .filter((v) => v != null)
    .slice(0, 5);

  if (samples.length === 0) return validKinds;

  // Check if Time format override is possible (e.g. String values that can be parsed as Time)
  if (originalFormat !== TableFormatOverrideKind.Time) {
    if (samples.every(canParseAsTime)) {
      validKinds.push(TableFormatOverrideKind.Time);
    }
  }

  // Check if Number format override is possible (e.g. String values that can be parsed as Number)
  // This enables proper numeric sorting for string columns containing numeric values.
  if (originalFormat !== TableFormatOverrideKind.Number) {
    if (samples.every(canParseAsNumber)) {
      validKinds.push(TableFormatOverrideKind.Number);
    }
  }

  // String is always valid as a fallback
  if (originalFormat !== TableFormatOverrideKind.String) {
    validKinds.push(TableFormatOverrideKind.String);
  }

  return validKinds;
}

type TimestampValue = number | string | null | undefined;

/**
 * Formats a timestamp value for display in table cells.
 */
export function formatTableTimestamp({
  value,
  timeZone,
  timeFormat,
}: {
  value: TimestampValue;
  timeZone?: string;
  timeFormat?: TimeFormat;
}): string {
  if (!value) return '--';

  // Convert high-precision timestamps to milliseconds for Date constructor
  // Nanoseconds have ~19 digits (>1e17), microseconds have ~16 digits (>1e14), milliseconds have ~13 digits
  let timestamp = value;
  if (typeof value === 'number') {
    if (value > 1e17) {
      // Nanoseconds to milliseconds
      timestamp = value / 1e6;
    } else if (value > 1e14) {
      // Microseconds to milliseconds
      timestamp = value / 1e3;
    }
  }

  return getFormattedDateTimeString({
    date: timestamp,
    timeZone,
    timeFormat: timeFormat || '24h',
    // Omit timezone abbreviation to save space in tables
    omitTimeZoneAbbreviation: true,
  });
}

/**
 * Converts a format override kind to a FormatOverrides object.
 * Returns undefined if kind is undefined (to clear the override).
 */
export function createFormatOverride(kind: TableFormatOverrideKind | undefined): FormatOverrides | undefined {
  if (kind === undefined) {
    return undefined;
  }

  switch (kind) {
    case TableFormatOverrideKind.Number:
      return { kind: TableFormatOverrideKind.Number };
    case TableFormatOverrideKind.Time:
      return { kind: TableFormatOverrideKind.Time };
    case TableFormatOverrideKind.String:
    default:
      return { kind: TableFormatOverrideKind.String };
  }
}
